import isEqual from 'lodash/isEqual'

import {
  BulkUpsertRetailerLocationsInput,
  LGSocialMediaAccounts,
  LocationType,
  RetailerProfileImage,
  SocialMediaType,
  UpdateRetailerProfileInput,
} from '__generated__/atlas-types'

import { fromGlobalId } from 'utils/transformations/graphql'

import {
  RetailerProfile,
  RetailerProfileLocation,
} from '../retailerProfilePage.types'
import {
  isNonJoorCarriedBrandId,
  normalizeBrandCarriedSelection,
} from 'features/LeadGen/brandCarriedName.utils'

export const EMPTY_SOCIAL_MEDIA: LGSocialMediaAccounts[] = [
  {
    type: SocialMediaType.INSTAGRAM,
    url: '',
  },
  {
    type: SocialMediaType.TWITTER,
    url: '',
  },
  {
    type: SocialMediaType.FACEBOOK,
    url: '',
  },
]

// A stored value that is just a page handle (no domain, no slashes) is a real
// value: it's what we append to www.facebook.com/. Anything else without a
// facebook.com/ segment is malformed.
export const FACEBOOK_BARE_TOKEN_PATTERN = /^[A-Za-z0-9._-]{1,50}$/
// Dots are legal in page handles ("joor.access"), so we only reject a bare
// value when it ends in a real TLD and is therefore a hostname - otherwise
// "facebook.com/" would resolve to the nonsense page www.facebook.com/facebook.com.
const LOOKS_LIKE_DOMAIN =
  /\.(com|net|org|co|io|me|uk|es|fr|de|it|us|ca|au|info|biz|tv|app)$/i
export const isBareFacebookToken = (value: string): boolean => {
  const trimmed = (value || '').trim()
  if (!FACEBOOK_BARE_TOKEN_PATTERN.test(trimmed)) return false
  return !LOOKS_LIKE_DOMAIN.test(trimmed)
}

const FACEBOOK_PAGE_URL = /^(https?:\/\/)?(www\.)?facebook\.com\/([^/\s]+)\/?$/i

// Resolves the real facebook page url from a stored value, or null when the
// value is neither a facebook.com/... url nor a bare page handle. Normalizes
// here (trim + strip surrounding slashes) so that every caller - validation and
// href building alike - resolves the exact same input and the two can never
// disagree.
export const resolveFacebookPageUrl = (url?: string | null): string | null => {
  const trimmed = (url || '').trim().replace(/^\/+|\/+$/g, '')
  if (!trimmed) return null
  const match = trimmed.match(FACEBOOK_PAGE_URL)
  if (match) return `https://www.facebook.com/${match[3]}`
  return isBareFacebookToken(trimmed)
    ? `https://www.facebook.com/${trimmed}`
    : null
}

// Facebook: a facebook.com/something url, or a bare page handle. Empty is valid
// because social media is optional.
const validateFacebookUrl = (url: string) => {
  const trimmed = (url || '').trim()
  if (!trimmed) return true
  return resolveFacebookPageUrl(trimmed) !== null
}

// Instagram/X: username only (no @) - 1–30 chars, letters, numbers, dots, underscores
const USERNAME_PATTERN = /^[A-Za-z0-9._]{1,30}$/
export const validateUsername = (username: string) => {
  const trimmed = (username || '').trim()
  return !trimmed || USERNAME_PATTERN.test(trimmed)
}

// Shared: requires protocol so links open correctly (used by Submissions BrandInfo/RecipientInfo)
// Allows path (/path), root-level query (?search=ropa), or both (/path?query)
export const validateWebsiteUrl = (url: string) => {
  const trimmed = (url || '').trim()
  if (!trimmed) return true
  return /^(https?|ftp):\/\/(www\.)?[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(\/[^\s]*|\?[^\s]*)?$/i.test(
    trimmed,
  )
}

// Retailer profile only: XXX.xxx format; http/https optional
// Allows path (/path), root-level query (?search=ropa), or both (/path?query)
export const validateRetailerProfileWebsiteUrl = (url: string) => {
  const trimmed = (url || '').trim()
  if (!trimmed) return true
  return /^(https?:\/\/)?[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(\/[^\s]*|\?[^\s]*)?$/i.test(
    trimmed,
  )
}

export const validateSocialMedia = (
  socialMediaType?: SocialMediaType | null,
  url?: string | null,
) => {
  switch (socialMediaType) {
    case SocialMediaType.FACEBOOK:
      return validateFacebookUrl(url || '')
    case SocialMediaType.INSTAGRAM:
    case SocialMediaType.TWITTER:
      return validateUsername(url || '')
    default:
      return validateUsername(url || '')
  }
}

// Normalize backend value to username only for display (right of the fixed @).
// Backend may send: full URL (instagram.com/foo), @username, or just the word (foo).
// All are valid; we show the username to the right of @ and save with @.
const INSTAGRAM_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)\/?$/i
const TWITTER_X_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?(?:twitter|x)\.com\/([A-Za-z0-9_]+)\/?$/i

export const backendUrlToDisplayUsername = (
  type: SocialMediaType,
  url: string | null | undefined,
): string => {
  const value = (url || '').trim()
  if (!value) return ''
  if (type === SocialMediaType.INSTAGRAM) {
    const match = value.match(INSTAGRAM_URL_REGEX)
    if (match) return match[1]
    return value.replace(/^@+/, '') // full URL already handled; strip @ or use word as-is
  }
  if (type === SocialMediaType.TWITTER) {
    const match = value.match(TWITTER_X_URL_REGEX)
    if (match) return match[1]
    return value.replace(/^@+/, '') // full URL already handled; strip @ or use word as-is
  }
  return value
}

/**
 * Returns whether a stored (type, url) is valid for display as a social link.
 * Applied to the raw stored value (before any frontend display transform).
 * For Instagram/Twitter we accept any form that normalizes to a valid username:
 * - "hola", "@hola", "https://x.com/hola", "www.x.com/hola" → all valid (username "hola").
 * We only reject when the effective username is invalid (e.g. "hello how are you", "bad!user").
 */
export const isValidStoredSocialMediaForDisplay = (
  type?: SocialMediaType | null,
  url?: string | null,
): boolean => {
  const value = (url || '').trim()
  if (!value) return false
  switch (type) {
    case SocialMediaType.FACEBOOK:
      return validateFacebookUrl(value)
    case SocialMediaType.INSTAGRAM: {
      const match = value.match(INSTAGRAM_URL_REGEX)
      const username = match
        ? match[1]
        : value
            .replace(/^@+/, '')
            .trim()
            .replace(/^\/+|\/+$/g, '')
      return validateUsername(username)
    }
    case SocialMediaType.TWITTER: {
      const match = value.match(TWITTER_X_URL_REGEX)
      const username = match
        ? match[1]
        : value
            .replace(/^@+/, '')
            .trim()
            .replace(/^\/+|\/+$/g, '')
      return validateUsername(username)
    }
    default:
      return false
  }
}

// Format username for save: @username for Instagram/X; Facebook URLs as https
export const formatSocialUrlForSave = (
  type: SocialMediaType,
  displayValue: string | null | undefined,
): string => {
  const trimmed = (displayValue || '').trim()
  if (!trimmed) return ''
  if (type === SocialMediaType.INSTAGRAM || type === SocialMediaType.TWITTER) {
    return trimmed.startsWith('@') ? trimmed : `@${trimmed}`
  }
  if (type === SocialMediaType.FACEBOOK && trimmed.startsWith('http://')) {
    return trimmed.replace(/^http:\/\//i, 'https://')
  }
  return trimmed
}

export const formatSocialMedia = (
  socialMediaList: RetailerProfile['socialMediaAccounts'] = [],
) => {
  const mappedMedia = EMPTY_SOCIAL_MEDIA.map((element) => {
    const backendUrl =
      socialMediaList?.filter((aux) => aux.type === element.type)[0]?.url || ''
    const displayUrl =
      element.type === SocialMediaType.INSTAGRAM ||
      element.type === SocialMediaType.TWITTER
        ? backendUrlToDisplayUsername(element.type, backendUrl)
        : backendUrl
    return {
      type: element.type,
      url: displayUrl,
    }
  })
  return mappedMedia
}

export const getInitialError = (
  socialMediaAccounts?: LGSocialMediaAccounts[] | null,
  websiteUrl?: string | null,
) => {
  const validWebsiteUrl =
    !Boolean(websiteUrl) || validateRetailerProfileWebsiteUrl(websiteUrl ?? '')
  const validSocialMedia = formatSocialMedia(socialMediaAccounts || [])
    .filter((media) => Boolean(media.url))
    .every((media) => validateSocialMedia(media.type, media.url))

  return !validWebsiteUrl || !validSocialMedia
}

export const getErrorMessage = (socialMediaType?: SocialMediaType | null) => {
  switch (socialMediaType) {
    case SocialMediaType.FACEBOOK:
      return 'Please use a valid Facebook URL (www.facebook.com/something)'
    case SocialMediaType.INSTAGRAM:
    case SocialMediaType.TWITTER:
      return 'Please enter a valid username (letters, numbers, dots, underscores)'
    default:
      return 'Please enter a valid username'
  }
}

export const checkMissingRequiredInfo = (
  locations: RetailerProfileLocation[],
) => {
  return locations.some(
    (location) => !location.country || !location.city || !location.address1,
  )
}

export const checkBasicInfoChanges = ({
  previousData,
  updatedData,
}: {
  previousData: RetailerProfile
  updatedData: RetailerProfile
}): boolean => {
  const descriptionChanges =
    previousData?.description !== updatedData?.description
  const logoChanges = previousData?.logo?.id !== updatedData?.logo?.id
  const websiteUrlChanges = previousData?.websiteUrl !== updatedData?.websiteUrl
  const socialMediaAccountsChanges = updatedData?.socialMediaAccounts?.some(
    (socialMediaAccount) => {
      const oldMediaAccount = previousData?.socialMediaAccounts?.find(
        (account) => account.type === socialMediaAccount.type,
      )
      return oldMediaAccount?.url !== socialMediaAccount?.url
    },
  )
  const taxIdChanges =
    (previousData?.taxId ?? '').trim() !== (updatedData?.taxId ?? '').trim()
  const taxIdCountryChanges =
    (previousData?.taxIdCountry ?? '').trim() !==
    (updatedData?.taxIdCountry ?? '').trim()

  return (
    descriptionChanges ||
    logoChanges ||
    websiteUrlChanges ||
    Boolean(socialMediaAccountsChanges) ||
    taxIdChanges ||
    taxIdCountryChanges
  )
}

export const getBasicInfoInput = (
  updatedData: RetailerProfile,
): UpdateRetailerProfileInput => {
  const description = updatedData?.description ?? ''
  const logoId = updatedData?.logo?.id ?? null
  const websiteUrl = updatedData?.websiteUrl ?? ''
  const socialMediaAccounts = updatedData?.socialMediaAccounts
  const taxId = updatedData?.taxId?.trim() ?? ''
  const taxIdCountry = updatedData?.taxIdCountry?.trim() ?? ''

  return {
    retailerId: updatedData?.id,
    description,
    logoId,
    websiteUrl,
    socialMediaAccounts,
    taxId,
    taxIdCountry,
  }
}

export const getTaxVerificationModalUpdateInput = ({
  retailerProfile,
  taxId,
  taxIdCountry,
}: {
  retailerProfile: RetailerProfile
  taxId: string
  taxIdCountry: string
}): UpdateRetailerProfileInput => ({
  retailerId: retailerProfile.id,
  description: retailerProfile.description ?? '',
  websiteUrl: retailerProfile.websiteUrl ?? '',
  taxId: taxId.trim(),
  taxIdCountry: taxIdCountry.trim(),
})

export const getProfileImagesInput = ({
  previousData,
  updatedData,
}: {
  previousData: RetailerProfile
  updatedData: RetailerProfile
}) => {
  const previousProfileImageIds = previousData?.profileImages?.map(
    (profileImage) => profileImage?.id,
  )
  const newProfileImages =
    updatedData?.profileImages?.filter(
      (previousImage) =>
        !previousProfileImageIds?.includes(previousImage.id || ''),
    ) || []
  return { uploads: newProfileImages?.map((newImage) => newImage.id) }
}

export const checkAddressChanges = ({
  previousLocation,
  updatedLocation,
}: {
  previousLocation: RetailerProfileLocation
  updatedLocation: RetailerProfileLocation
}): boolean => {
  const updatedName = previousLocation.name !== updatedLocation.name
  const updatedAddress1 = previousLocation.address1 !== updatedLocation.address1
  const updatedAddress2 = previousLocation.address2 !== updatedLocation.address2
  const updatedCity = previousLocation.city !== updatedLocation.city
  const updatedState = previousLocation.state !== updatedLocation.state
  const updatedZip = previousLocation.zip !== updatedLocation.zip
  const updatedCountry = previousLocation.country !== updatedLocation.country
  const updatedPhone = previousLocation.phone !== updatedLocation.phone
  const updatedYearEstablished =
    previousLocation.yearEstablished !== updatedLocation.yearEstablished
  const updatedWholesaleMin =
    previousLocation.wholesaleRange?.min !== updatedLocation.wholesaleRange?.min
  const updatedWholesaleMax =
    previousLocation.wholesaleRange?.max !== updatedLocation.wholesaleRange?.max
  const typeChanges = previousLocation.type !== updatedLocation.type

  return (
    updatedName ||
    updatedAddress1 ||
    updatedAddress2 ||
    updatedCity ||
    updatedState ||
    updatedZip ||
    updatedCountry ||
    updatedPhone ||
    updatedYearEstablished ||
    updatedWholesaleMin ||
    updatedWholesaleMax ||
    typeChanges
  )
}

export const checkProductLineChanges = ({
  previousLocation,
  updatedLocation,
}: {
  previousLocation: RetailerProfileLocation
  updatedLocation: RetailerProfileLocation
}): boolean => {
  const previousCategories =
    previousLocation?.categories?.map((category) => category?.id) || []
  const updatedCategories =
    updatedLocation?.categories?.map((category) => category?.id) || []
  return !isEqual(previousCategories, updatedCategories)
}

export const checkCarriedBrandsChanges = ({
  previousLocation,
  updatedLocation,
}: {
  previousLocation: RetailerProfileLocation
  updatedLocation: RetailerProfileLocation
}): boolean => {
  const previousCarriedBrands =
    previousLocation?.brandsCarried?.map((brandCarried) => brandCarried?.id) ||
    []
  const updatedCarriedBrands =
    updatedLocation?.brandsCarried?.map((brandCarried) => brandCarried?.id) ||
    []
  return !isEqual(previousCarriedBrands, updatedCarriedBrands)
}

export const checkClienteleChanges = ({
  previousLocation,
  updatedLocation,
}: {
  previousLocation: RetailerProfileLocation
  updatedLocation: RetailerProfileLocation
}) => {
  const clienteleMaleChanges =
    previousLocation.clienteleInfo?.male !== updatedLocation.clienteleInfo?.male
  const clienteleFemaleChanges =
    previousLocation.clienteleInfo?.female !==
    updatedLocation.clienteleInfo?.female
  const clienteleAgeChanges =
    previousLocation.clienteleInfo?.age !== updatedLocation.clienteleInfo?.age
  const previousClienteleDescriptions =
    previousLocation?.clienteleInfo?.descriptions?.map(
      (clienteleDesc) => clienteleDesc?.id,
    ) || []
  const updatedClienteleDescriptions =
    updatedLocation?.clienteleInfo?.descriptions?.map(
      (clienteleDesc) => clienteleDesc?.id,
    ) || []
  const clienteleDescriptionsChange = !isEqual(
    previousClienteleDescriptions,
    updatedClienteleDescriptions,
  )
  const previousClienteleInterests =
    previousLocation?.clienteleInfo?.interests?.map(
      (clienteleInt) => clienteleInt?.id,
    ) || []
  const updatedClienteleInterests =
    updatedLocation?.clienteleInfo?.interests?.map(
      (clienteleInt) => clienteleInt?.id,
    ) || []
  const clienteleInterestsChange = !isEqual(
    previousClienteleInterests,
    updatedClienteleInterests,
  )
  return (
    clienteleMaleChanges ||
    clienteleFemaleChanges ||
    clienteleAgeChanges ||
    clienteleDescriptionsChange ||
    clienteleInterestsChange
  )
}

export const checkLocationChanges = ({
  previousLocation,
  updatedLocation,
}: {
  previousLocation: RetailerProfileLocation
  updatedLocation: RetailerProfileLocation
}): boolean =>
  checkAddressChanges({ previousLocation, updatedLocation }) ||
  checkProductLineChanges({ previousLocation, updatedLocation }) ||
  checkClienteleChanges({ previousLocation, updatedLocation }) ||
  checkCarriedBrandsChanges({ previousLocation, updatedLocation })

export const checkLocationsChanges = ({
  previousLocations,
  updatedLocations,
}: {
  previousLocations?: RetailerProfileLocation[] | null
  updatedLocations?: RetailerProfileLocation[] | null
}): RetailerProfileLocation[] => {
  let locationsToUpdate = [] as RetailerProfileLocation[]
  updatedLocations?.map((updatedLocation) => {
    const oldLocation =
      previousLocations?.find(
        (previousLocation) => previousLocation.id === updatedLocation.id,
      ) || ({} as RetailerProfileLocation)
    const changesOnLocation = checkLocationChanges({
      previousLocation: oldLocation,
      updatedLocation,
    })
    if (changesOnLocation) {
      locationsToUpdate.push(updatedLocation)
    }
  })
  return locationsToUpdate
}

export const getLocationsToDelete = ({
  previousLocations,
  updatedLocations,
}: {
  previousLocations?: RetailerProfileLocation[] | null
  updatedLocations?: RetailerProfileLocation[] | null
}): RetailerProfileLocation[] => {
  const updatedLocationIds = updatedLocations?.map(
    (updatedLocation) => updatedLocation.id,
  )
  const locationsToDelete =
    previousLocations?.filter((previousLocation) => {
      return !updatedLocationIds?.includes(previousLocation.id)
    }) || []
  return locationsToDelete
}

export const getDeleteLocationsInput = (
  locations: RetailerProfileLocation[],
) => ({
  retailerLocationIds: locations.map((location) => location.id),
})

export const getNewProfileImages = ({
  previousData,
  updatedData,
}: {
  previousData: RetailerProfile
  updatedData: RetailerProfile
}): RetailerProfileImage[] => {
  const previousProfileImageIds = previousData?.profileImages?.map(
    (profileImage) => profileImage?.id,
  )

  const newProfileImages =
    updatedData?.profileImages?.filter(
      (previousImage) =>
        !previousProfileImageIds?.includes(previousImage?.id || ''),
    ) || []

  return newProfileImages
}

export const getProfileImagesToDelete = ({
  previousData,
  updatedData,
}: {
  previousData: RetailerProfile
  updatedData: RetailerProfile
}): RetailerProfileImage[] => {
  const updatedProfileImageIds = updatedData?.profileImages?.map(
    (profileImage) => profileImage.id,
  )
  const profileImagesToDelete =
    previousData?.profileImages?.filter(
      (previousImage) =>
        !updatedProfileImageIds?.includes(previousImage?.id || ''),
    ) || []
  return profileImagesToDelete
}

export const getDeleteProfileImagesInput = (
  profileImages: RetailerProfileImage[],
) => ({
  retailerUploadIds: profileImages.map((profileImage) => profileImage.id),
})

// atob throws InvalidCharacterError instead of returning a falsy value when the
// string is not valid base64, so we wrap fromGlobalId to safely distinguish
// real relay IDs from user-typed brand names without crashing the save flow.
const isValidRelayId = (id: string): boolean => {
  try {
    return Boolean(fromGlobalId(id).id)
  } catch {
    //need this so it doesnt throw an exception
    return false
  }
}

export const getLocationsInput = (
  locations?: RetailerProfileLocation[] | null,
): BulkUpsertRetailerLocationsInput => {
  const updatedLocations = locations?.map((location) => ({
    ...(!location.id.includes('new') && { id: location.id }),
    name: location.name ?? '',
    isPrimaryLocation: location?.isPrimary || false,
    type: location.type ?? LocationType.STORE,
    address1: location.address1 ?? '',
    address2: location.address2 ?? '',
    city: location.city ?? '',
    state: location.state ?? '',
    zip: location.zip ?? '',
    country: location.country ?? '',
    phone: location.phone ?? '',
    yearEstablished: location.yearEstablished,
    wholesaleMin: location.wholesaleRange?.min ?? null,
    wholesaleMax: location.wholesaleRange?.max ?? null,
    carriedBrands: location.brandsCarried
      ?.filter(
        (brandCarried) =>
          Boolean(brandCarried?.id) && isValidRelayId(brandCarried.id),
      )
      ?.map((brandCarried) => brandCarried!.id as string),
    nonJoorCarriedBrands: location.brandsCarried
      ?.filter(
        (brandCarried) =>
          isNonJoorCarriedBrandId(brandCarried?.id ?? '') &&
          normalizeBrandCarriedSelection(brandCarried).length > 0,
      )
      ?.map((brandCarried) => normalizeBrandCarriedSelection(brandCarried)),
    categories: location.categories?.map((category) => category?.id),
    clienteleInfo: {
      clienteleMale: location?.clienteleInfo?.male,
      clienteleFemale: location?.clienteleInfo?.female,
      clienteleAge: location?.clienteleInfo?.age,
      clienteleDescriptions: location.clienteleInfo?.descriptions?.map(
        (clienteleDesc) => clienteleDesc?.id,
      ),
      clienteleInterests: location.clienteleInfo?.interests?.map(
        (clienteleInt) => clienteleInt?.id,
      ),
    },
  }))
  return { locations: updatedLocations || [] }
}
